import { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Gem, Flame, Sparkles, X } from 'lucide-react';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const POLL_MS = 20_000;
const CELEBRATION_TTL = 12_000; // banner stays for 12s
const SEEN_KEY = 'betohalics:fires_seen';

const FIRE_META = {
    grand: { label: 'COMMUNITY GRAND', color: '#FFD700', icon: Trophy, accent: '#FFD700' },
    super: { label: 'DAILY SUPER',     color: '#FF3B30', icon: Flame,  accent: '#FF6B6B' },
    blake: { label: "BETOHALICS BUCKS", color: '#FF007F', icon: Gem,  accent: '#FF007F' },
};

function loadSeen() {
    try {
        const raw = localStorage.getItem(SEEN_KEY);
        if (!raw) return new Set();
        return new Set(JSON.parse(raw));
    } catch { return new Set(); }
}
function saveSeen(set) {
    try { localStorage.setItem(SEEN_KEY, JSON.stringify(Array.from(set).slice(-50))); } catch { /* ignore */ }
}

const fireConfettiBlast = (color) => {
    const defaults = { startVelocity: 35, spread: 360, ticks: 80, zIndex: 100 };
    const colors = [color, '#FFFFFF', '#FFD700'];
    const burst = (origin) => confetti({ ...defaults, particleCount: 70, origin, colors });
    burst({ x: 0.2, y: 0.4 });
    burst({ x: 0.8, y: 0.4 });
    burst({ x: 0.5, y: 0.2 });
    setTimeout(() => burst({ x: 0.5, y: 0.5 }), 350);
};

/**
 * JackpotFireCelebration
 * ----------------------
 * Polls /api/jackpot/recent-fires every 20s. When a new fire is detected
 * (not in localStorage seen-set), shows either:
 *   - PERSONAL banner + confetti (if I won a share)
 *   - COMMUNITY banner (if I didn't, but the pot just dropped)
 *
 * Banner auto-dismisses after 12s; user can also close manually.
 * Mounted globally inside <App />.
 */
const JackpotFireCelebration = () => {
    const { user } = useAuth();
    const [active, setActive] = useState(null); // {fire_id, type, my_share, distributed, winner_count, i_won}
    const seenRef = useRef(loadSeen());
    const dismissTimerRef = useRef(null);
    const initializedRef = useRef(false);

    const dismiss = useCallback(() => {
        clearTimeout(dismissTimerRef.current);
        setActive(null);
    }, []);

    useEffect(() => {
        if (!user || user.role === 'admin') return; // players only
        let cancelled = false;

        const poll = async () => {
            try {
                const { data } = await axios.get(`${API}/jackpot/recent-fires`, { withCredentials: true });
                if (cancelled || !Array.isArray(data) || !data.length) return;

                // First poll after login: seed seen-set with everything (don't replay history)
                if (!initializedRef.current) {
                    initializedRef.current = true;
                    data.forEach((f) => seenRef.current.add(f.fire_id));
                    saveSeen(seenRef.current);
                    return;
                }

                // Find the most recent fire we haven't shown yet
                const fresh = data.find((f) => !seenRef.current.has(f.fire_id));
                if (!fresh) return;

                seenRef.current.add(fresh.fire_id);
                saveSeen(seenRef.current);

                setActive(fresh);
                clearTimeout(dismissTimerRef.current);
                dismissTimerRef.current = setTimeout(() => setActive(null), CELEBRATION_TTL);

                // Confetti for personal wins
                if (fresh.i_won) {
                    fireConfettiBlast(FIRE_META[fresh.type]?.color || '#FFD700');
                }
            } catch { /* silent */ }
        };

        poll();
        const id = setInterval(poll, POLL_MS);
        return () => {
            cancelled = true;
            clearInterval(id);
            clearTimeout(dismissTimerRef.current);
        };
    }, [user]);

    if (!active) return null;
    const meta = FIRE_META[active.type] || FIRE_META.grand;
    const Icon = meta.icon;
    const isWinner = !!active.i_won;

    return (
        <div className="fixed inset-x-0 top-0 z-[200] flex justify-center px-3 pointer-events-none" data-testid="jackpot-fire-celebration">
            <AnimatePresence>
                <motion.div
                    key={active.fire_id}
                    initial={{ opacity: 0, y: -100, scale: 0.85 }}
                    animate={{ opacity: 1, y: 16, scale: 1 }}
                    exit={{ opacity: 0, y: -100 }}
                    transition={{ type: 'spring', stiffness: 280, damping: 22 }}
                    className="relative pointer-events-auto max-w-2xl w-full"
                >
                    <div
                        className="relative rounded-2xl overflow-hidden border-2 backdrop-blur-md shadow-2xl"
                        style={{
                            background: `linear-gradient(135deg, ${meta.color}25, rgba(0,0,0,0.85) 60%)`,
                            borderColor: meta.color,
                            boxShadow: `0 20px 60px rgba(0,0,0,0.7), 0 0 50px ${meta.color}80`,
                        }}
                    >
                        {/* Animated shimmer band */}
                        <div className="jackpot-fire-shimmer" aria-hidden="true" />
                        <div className="relative p-5 sm:p-6 flex items-start gap-4">
                            <motion.div
                                animate={{ rotate: [0, -8, 8, -6, 6, 0], scale: [1, 1.15, 1.15, 1] }}
                                transition={{ duration: 1.2, repeat: Infinity, repeatDelay: 0.6 }}
                                className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center"
                                style={{
                                    background: `linear-gradient(135deg, ${meta.color}, ${meta.accent})`,
                                    boxShadow: `0 0 28px ${meta.color}aa`,
                                }}
                            >
                                <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-black" />
                            </motion.div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                    <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-black" style={{ color: meta.color }}>
                                        🎉 {meta.label} JUST DROPPED
                                    </span>
                                </div>
                                {isWinner ? (
                                    <>
                                        <h2 className="text-xl sm:text-3xl font-black text-white leading-tight" data-testid="celebration-personal-title">
                                            You won <span className="font-mono" style={{ color: meta.color, textShadow: `0 0 18px ${meta.color}` }}>${active.my_share.toFixed(2)}</span>
                                            <Sparkles className="inline-block w-5 h-5 ml-1.5 text-[#FFD700]" />
                                        </h2>
                                        <p className="text-[12px] sm:text-sm text-white/85 mt-1">
                                            Your share has been added to your balance.
                                            {active.winner_count > 1 && ` Split across ${active.winner_count} eligible spinners.`}
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <h2 className="text-lg sm:text-2xl font-black text-white leading-tight" data-testid="celebration-community-title">
                                            <span className="font-mono" style={{ color: meta.color }}>${active.distributed.toFixed(2)}</span>
                                            <span> just paid out to </span>
                                            <span style={{ color: meta.color }}>{active.winner_count}</span>
                                            <span> player{active.winner_count === 1 ? '' : 's'}</span>
                                        </h2>
                                        <p className="text-[12px] sm:text-sm text-white/80 mt-1">
                                            Place a wager to be in the next pot when it fires.
                                        </p>
                                    </>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={dismiss}
                                className="shrink-0 -mr-1 -mt-1 p-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-colors"
                                aria-label="Dismiss"
                                data-testid="celebration-dismiss"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </motion.div>
            </AnimatePresence>
        </div>
    );
};

export default JackpotFireCelebration;
